import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link, } from 'react-router-dom';

import DetailsModal from '@/components/DetailsModal';
import WorldMap from '@/components/WorldMap';
import { getCountryName, getCountryFlag } from './countryCodeMapping';

import { ArrowUpRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LocationsInsights = ({ websiteId, period = '24h', startDate, endDate, channel, device, os, browser, token, analytics, existingFilters = [] }) => {
        const navigate = useNavigate();

        // Auth header helper — token is null in publicMode; backend analytics endpoints
        // accept unauthenticated reads on public sites via get_current_user_optional.
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

        // Create stable filter string to prevent infinite re-renders
        const filtersKey = useMemo(() => existingFilters.join('|'), [existingFilters]);
        const [activeTab, setActiveTab] = useState('map');
        const [showModal, setShowModal] = useState(false);
        const [limit, setLimit] = useState(9); // Default to top 3
        const [modalData, setModalData] = useState([]);
        const [modalLoading, setModalLoading] = useState(false);
        const [searchQuery, setSearchQuery] = useState('');
        const [sortConfig, setSortConfig] = useState({ key: 'visitors', direction: 'desc' });
        
        // Handle location click to filter dashboard - using unified ?f= format
        const handleLocationClick = (locationType, locationName) => {
                console.log(locationName, locationType);
                
                // Build URL with new unified filter format
                let url = `/analytics/${websiteId}?period=${period}`;
                if (startDate && endDate) {
                        url += `&start_date=${startDate}&end_date=${endDate}`;
                }
                
                // Keep existing f filters
                existingFilters.forEach(f => {
                        url += `&f=${encodeURIComponent(f)}`;
                });
                
                // Add new location filter in unified format
                // Map locationType to filter type: country, region, city
                const filterType = locationType; // Already: country, region, or city
                url += `&f=is,${filterType},${encodeURIComponent(locationName)}`;
                
                navigate(url);
        };

        

        const getData = () => {
                if (!analytics) return [];

                if (activeTab === 'countries') {
                        return Object.entries(analytics.countries || {})
                                .map(([code, count]) => ({ code, name: getCountryName(code), count }))
                                .sort((a, b) => b.count - a.count);
                } else if (activeTab === 'regions') {
                        // Prefer the new regions_detail array (has country code for
                        // flag rendering). Fall back to the legacy {name: count} dict
                        // when talking to a backend that predates the additive change.
                        if (Array.isArray(analytics.regions_detail) && analytics.regions_detail.length) {
                                return analytics.regions_detail
                                        .map((r) => ({ name: r.name, country: r.country, count: r.visitors }))
                                        .sort((a, b) => b.count - a.count);
                        }
                        return Object.entries(analytics.regions || {})
                                .map(([name, count]) => ({ name, count }))
                                .sort((a, b) => b.count - a.count);
                } else if (activeTab === 'cities') {
                        // Same additive fallback as regions.
                        if (Array.isArray(analytics.cities_detail) && analytics.cities_detail.length) {
                                return analytics.cities_detail
                                        .map((r) => ({ name: r.name, country: r.country, count: r.visitors }))
                                        .sort((a, b) => b.count - a.count);
                        }
                        return Object.entries(analytics.cities || {})
                                .map(([name, count]) => ({ name, count }))
                                .sort((a, b) => b.count - a.count);
                }
                return [];
        };
        
        // Get total count based on active tab
        const getTotalCount = () => {
                if (!analytics) return 0;
                if (activeTab === 'countries') {
                        return analytics.total_countries || Object.keys(analytics.countries || {}).length;
                } else if (activeTab === 'regions') {
                        return analytics.total_regions || Object.keys(analytics.regions || {}).length;
                } else if (activeTab === 'cities') {
                        return analytics.total_cities || Object.keys(analytics.cities || {}).length;
                }
                return 0;
        };

        const data = getData();
        const displayData = data.slice(0, limit);
        const sectionTotalCount = getTotalCount();
        const total = Object.values(analytics?.countries || {}).reduce((a, b) => a + b, 0) ||
                Object.values(analytics?.regions || {}).reduce((a, b) => a + b, 0) ||
                Object.values(analytics?.cities || {}).reduce((a, b) => a + b, 0);


        // Fetch all data for modal when it opens
        useEffect(() => {
                const fetchModalData = async () => {
                        // Public viewers have no token; backend allows unauthenticated reads
                        // for is_public sites — only bail if there's no website to query.
                        if (!showModal || !websiteId) return;

                        setModalLoading(true);

                        try {
                                let url = `${BACKEND_URL}/api/analytics/${websiteId}/details?type=locations&tab=${activeTab}&period=${period}&all=true`;
                                if (startDate && endDate) {
                                        url += `&start_date=${startDate}&end_date=${endDate}`;
                                }
                                
                                if (channel) {
                                        url += `&channel=${encodeURIComponent(channel)}`;
                                }
                                // Note: location filter is now handled via existingFilters (f params)
                                if (device) {
                                        url += `&device=${encodeURIComponent(device)}`;
                                }
                                if (os) {
                                        url += `&os=${encodeURIComponent(os)}`;
                                }
                                if (browser) {
                                        url += `&browser=${encodeURIComponent(browser)}`;
                                }

                                // Pass unified f filters
                                if (existingFilters && existingFilters.length > 0) {
                                        existingFilters.forEach(f => {
                                                url += `&f=${encodeURIComponent(f)}`;
                                        });
                                }

                                const response = await axios.get(url, { headers: authHeaders });
                                setModalData(response.data.records || []);
                        } catch (err) {
                                console.error('Error fetching modal data:', err);
                                setModalData([]);
                        } finally {
                                setModalLoading(false);
                        }
                };

                fetchModalData();
        }, [showModal, websiteId, period, startDate, endDate, token, filtersKey, activeTab, channel, device, os, browser]);

        // Filter and sort modal data
        const filteredModalData = modalData.filter(item => {
                if (!searchQuery.trim()) return true;
                const query = searchQuery.toLowerCase();
                return (
                        item.country?.toLowerCase().includes(query) ||
                        item.region?.toLowerCase().includes(query) ||
                        item.city?.toLowerCase().includes(query)
                );
        });

        const sortedModalData = [...filteredModalData].sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];


                if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }

                const aStr = String(aVal).toLowerCase();
                const bStr = String(bVal).toLowerCase();

                if (sortConfig.direction === 'asc') {
                        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
                } else {
                        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
                }
        });


        // Handle sorting
        const handleSort = (key) => {
                setSortConfig(prev => ({
                        key,
                        direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
                }));
        };

        return (
                <>
                        <div className="data-table-container">
                                <div className="table-header">
                                        <h3 className="table-title">Locations</h3>
                                        <div className="table-tabs">
                                                <button
                                                        className={`table-tab ${activeTab === 'map' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab('map')}
                                                        data-testid="tab-map"
                                                >
                                                        Map
                                                </button>
                                                <button
                                                        className={`table-tab ${activeTab === 'countries' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab('countries')}
                                                        data-testid="tab-countries"
                                                >
                                                        Countries
                                                </button>
                                                <button
                                                        className={`table-tab ${activeTab === 'regions' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab('regions')}
                                                        data-testid="tab-regions"
                                                >
                                                        Regions
                                                </button>
                                                <button
                                                        className={`table-tab ${activeTab === 'cities' ? 'active' : ''}`}
                                                        onClick={() => setActiveTab('cities')}
                                                        data-testid="tab-cities"
                                                >
                                                        Cities
                                                </button>
                                        </div>
                                </div>

                                <div className="table-content">
                                        {/* Map Tab */}
                                        {activeTab === 'map' && (
                                                <div style={{ padding: '10px 0' }}>
                                                        <WorldMap
                                                                data={analytics?.countries || {}}
                                                                loading={!analytics || Object.keys(analytics).length === 0}
                                                        />
                                                </div>
                                        )}

                                        {/* Countries Tab */}
                                        {activeTab === 'countries' && (
                                                <>
                                                        {displayData.length === 0 ? (
                                                                <div className="table-row">
                                                                        <div className="table-cell">
                                                                                <span className="no-data">No country data available</span>
                                                                        </div>
                                                                </div>
                                                        ) : (

                                                                displayData.map((item, idx) => {

                                                                        const percentage = ((item.count / total) * 100).toFixed(1);
                                                                        return (
                                                                                <div key={idx} className="table-row" data-testid={`country-${item.code}`}>
                                                                                        <div
                                                                                                className="table-cell location-cell clickable-location"
                                                                                                onClick={() => handleLocationClick('country', item.code)}
                                                                                                style={{ cursor: 'pointer', color: '#6366f1' }}
                                                                                        >
                                                                                                <span className="country-flag">{getCountryFlag(item.code)}</span>
                                                                                                <span>{item.name}</span>
                                                                                                
                                                                                        </div>
                                                                                        <div className="table-cell number-cell">
                                                                                                <span>{item.count.toLocaleString()}</span>
                                                                                                <span className="percentage">{percentage}%</span>
                                                                                        </div>
                                                                                </div>
                                                                        );
                                                                })
                                                        )}
                                                </>
                                        )}

                                        {/* Regions Tab */}
                                        {activeTab === 'regions' && (
                                                <>
                                                        {displayData.length === 0 ? (
                                                                <div className="table-row">
                                                                        <div className="table-cell">
                                                                                <span className="no-data">No region data available</span>
                                                                        </div>
                                                                </div>
                                                        ) : (
                                                                displayData.map((item, idx) => {
                                                                        const percentage = ((item.count / total) * 100).toFixed(1);
                                                                        return (
                                                                                <div key={idx} className="table-row" data-testid={`region-${item.name}`}>
                                                                                        <div
                                                                                                className="table-cell location-cell clickable-location"
                                                                                                onClick={() => handleLocationClick('region', item.name)}
                                                                                                style={{ cursor: 'pointer', color: '#6366f1' }}
                                                                                        >
                                                                                                {item.country && (
                                                                                                        <span className="country-flag" style={{ marginRight: '8px' }}>{getCountryFlag(item.country)}</span>
                                                                                                )}
                                                                                                <span>{item.name}</span>
                                                                                        </div>
                                                                                        <div className="table-cell number-cell">
                                                                                                <span>{item.count.toLocaleString()}</span>
                                                                                                <span className="percentage">{percentage}%</span>
                                                                                        </div>
                                                                                        <div className="table-bar">
                                                                                                <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                                                                                        </div>
                                                                                </div>
                                                                        );
                                                                })
                                                        )}
                                                </>
                                        )}

                                        {/* Cities Tab */}
                                        {activeTab === 'cities' && (
                                                <>
                                                        {displayData.length === 0 ? (
                                                                <div className="table-row">
                                                                        <div className="table-cell">
                                                                                <span className="no-data">No city data available</span>
                                                                        </div>
                                                                </div>
                                                        ) : (
                                                                displayData.map((item, idx) => {
                                                                        const percentage = ((item.count / total) * 100).toFixed(1);
                                                                        return (
                                                                                <div key={idx} className="table-row" data-testid={`city-${item.name}`}>
                                                                                        <div
                                                                                                className="table-cell location-cell clickable-location"
                                                                                                onClick={() => handleLocationClick('city', item.name)}
                                                                                                style={{ cursor: 'pointer', color: '#6366f1' }}
                                                                                        >
                                                                                                {item.country && (
                                                                                                        <span className="country-flag" style={{ marginRight: '8px' }}>{getCountryFlag(item.country)}</span>
                                                                                                )}
                                                                                                <span>{item.name}</span>
                                                                                        </div>
                                                                                        <div className="table-cell number-cell">
                                                                                                <span>{item.count.toLocaleString()}</span>
                                                                                                <span className="percentage">{percentage}%</span>
                                                                                        </div>
                                                                                        <div className="table-bar">
                                                                                                <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                                                                                        </div>
                                                                                </div>
                                                                        );
                                                                })
                                                        )}
                                                </>
                                        )}
                                </div>

                                {activeTab !== 'map' && sectionTotalCount > limit && (
                                        <button className="table-details" onClick={() => setShowModal(true)}>
                                                ↻ DETAILS
                                        </button>
                                )}
                        </div>

                        {/* Details Modal */}
                        <DetailsModal
                                isOpen={showModal}
                                onClose={() => {
                                        setShowModal(false);
                                        setSearchQuery('');
                                        setSortConfig({ key: 'visitors', direction: 'desc' });
                                }}
                                device={device}
                                period={period}
                                startDate={startDate}
                                endDate={endDate}
                                websiteId={websiteId}
                                os={os}
                                browser={browser}
                                channel={channel}
                                title={`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} - All Results`}
                                type="locations"
                                data={sortedModalData}
                                loading={modalLoading}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                                activeTab={activeTab}
                        />
                </>
        );
};

export default LocationsInsights;
